import { useState, useEffect, useRef, useCallback, useMemo, Fragment } from "react";
import { useDispatch } from "react-redux";
import {
  Box, Button, Select, MenuItem, TextField, InputAdornment, Tooltip,
  FormControlLabel, Radio,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { addMonths } from "date-fns";
import { withTranslation } from "react-i18next";

import AppCard, { CARD_HEADER_SX, SECTION_TITLE_SX } from "@plannel/components/ui/AppCard";
import StyledSwitch from "@plannel/components/ui/StyledSwitch";
import { CloseButton } from "@plannel/components/ActionIconButton";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog";
import planConfigService from "@plannel/services/data/plan-config-service";
import bfAlgoRuleService from "@plannel/services/dp/bf-algo-rule-service";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import codeUtil from "@plannel/pages/utils/code-util";
import colorUtil from "@plannel/pages/utils/color-util";
import dateUtils from "@plannel/utils/date-util";
import BfAlgorithmSettings from "@plannel/pages/demand-plan/dp-config/BfAlgorithmSettings";
import { FS } from "@plannel/utils/font-util";

/*
 * 수요예측(BF) 설정.
 *
 * 화면은 세 칸이다 — 왼쪽 스텝 내비 / 가운데 단일 스크롤 본문 / 오른쪽 "적용 요약".
 * 예전에는 5개 아코디언이었는데, 접혀 있으면 지금 설정이 어떤 상태인지 알 수 없어서
 * 저장 전에 매번 다섯 개를 다 펴 보아야 했다. 스텝은 항상 펼쳐 두고, "무엇이 켜져 있는지"는
 * 왼쪽 내비의 한 줄 요약과 오른쪽 요약 칸이 대신 말한다.
 *
 * 오른쪽 칸은 저장 버튼도 갖는다. 저장은 "지금 무엇이 바뀌는지"를 본 뒤에 누르는 행동이므로,
 * 변경 사항·확인 필요 목록 바로 아래에 둔다(예전에는 스크롤 맨 끝에 버튼 하나였다).
 *
 * 저장 페이로드(plan_config.objVal)와 룰 저장(z_bf_algo_rule) 계약은 그대로다 — 화면만 바뀐다.
 */

const DIALOG_TITLE = { SAVE: "SAVE" };
const LEVELS = ["top", "middle", "bottom"];
const STEP_IDS = { S1: "bf-step-1", S2: "bf-step-2", S3: "bf-step-3", S4: "bf-step-4", S5: "bf-step-5" };
const TRAINING_TYPE = { PERIOD: "period", DATE: "date", LAST_BUCKET_FULL_FLAG: "isLastBucketFull" };

const INIT_HRCHY = { CTIT: false, CTIM: true, CTIB: true, CMIT: false, CMIM: true, CMIB: true, CBIT: false, CBIM: true, CBIB: false };

/** 알고리즘별 추가 파라미터 종류. 여기 없는 알고리즘은 체크만 하면 된다. */
const ALGO_PARAM = { RF: "quantile", XGB: "quantile", ZBASE: "baseline", ZSMA: "window", SMA: "window" };

const hrchyKey = (cust, item) => `C${cust[0].toUpperCase()}I${item[0].toUpperCase()}`;

/* 필드는 34px 로 통일한다 — MUI small(40px)은 한 행에 라벨+입력을 겹쳐 쌓는 이 밀도에서 너무 크다. */
/*
 * 입력칸 한 줄의 규격. **자손과 자기 자신을 함께** 겨눠야 한다.
 * TextField 는 FormControl 이 루트라 입력칸(.MuiInputBase-root)이 자손이지만,
 * Select 는 자기 자신이 입력칸이라 자손 선택자에 걸리지 않는다.
 * 한쪽만 적으면 Select 만 MUI 기본 높이로 남아(실측 38.56px) 옆 TextField(34px)와 키가 어긋난다.
 */
/*
 * 이 화면의 입력칸은 전부 **밑줄**이다(variant="standard").
 * 설정 한 줄은 대개 "라디오/체크박스 + 라벨 + 값" 인데, 값을 테두리 상자로 두르면 그 상자가
 * 정작 고르는 컨트롤보다 세게 보이고, 값이 여럿 겹쳐 서면 상자들이 한 덩어리로 뭉쳐 읽힌다.
 * 밑줄이면 값이라는 것만 알려 주고 물러난다.
 *
 * **자손과 자기 자신을 함께** 겨눠야 한다 — TextField 는 FormControl 이 루트라 입력칸이 자손이지만,
 * Select 는 자기 자신이 입력칸이라 자손 선택자에 걸리지 않는다. 한쪽만 적으면 Select 만
 * MUI 기본 높이로 남아 옆 TextField 와 키가 어긋난다.
 */
const FIELD_SX = {
  "& .MuiInputBase-root, &.MuiInputBase-root": { height: 34, fontSize: FS.SUB },
  "& .MuiInputBase-input": { fontSize: FS.SUB },
};

/*
 * 학습 기간의 두 입력칸(개월 수 / 시작일).
 *
 * 폭: 세로로 겹쳐 서는 칸이라 폭이 다르면 — 높이는 같아도 — 오른쪽 끝이 어긋나 "키가 다르다"로 읽힌다.
 *     둘 중 넓은 쪽(날짜 yyyy-MM-dd + 달력 아이콘)에 맞춘다.
 * 모양: 테두리 상자가 아니라 **밑줄**이다. 라디오 옆에 딸린 값이라 상자로 두르면 라디오보다
 *      입력칸이 더 세게 보이고, 두 줄이 겹쳐 서면 상자 두 개가 한 덩어리처럼 뭉친다.
 */
const TRAINING_FIELD_WIDTH = 160;


/*
 * 카드 안의 구역 제목("결측치"·"적용 중인 모델" 등). 카드 제목 표준(TITLE, 13px)은 본문(SUB, 12px)과
 * 1px 차이라 이 밀도에서는 위계가 안 보인다 — 한 칸 건너뛴 H2 로 올린다(타이포 표준 §2).
 */
const SUB_HEADER_SX = { ...CARD_HEADER_SX, fontSize: FS.H2 };

/*
 * 예측 카드는 **뷰포트가 아니라 제 폭으로** 배치를 정한다.
 * 이 화면은 좌우에 고정 패널(내비 220 + 요약 340)을 달고 있어서, 같은 뷰포트에서도 본문에
 * 남는 폭이 560px 넘게 차이 난다. MUI 의 md/lg 로 끊으면 "뷰포트는 넓은데 칸은 좁은" 구간을
 * 통째로 놓친다(모델 목록이 잘리던 게 정확히 그 구간이었다).
 */
const S3_SPLIT_AT = "@container (min-width: 960px)";
/*
 * STEP 02 의 두 칸(결측치 | 이상치)이 나란히 설 수 있는 폭.
 * ⚠ 320(왼쪽 고정) + 오른쪽 최소만으로 잡으면 안 된다 — 오른쪽 칸에는 "라디오 라벨 + 입력칸 160 +
 *   범위 안내" 한 줄이 들어가서 ~380px 이 필요하다. 그보다 빠듯하게 끊으면 칸은 갈라졌는데
 *   오른쪽 줄이 깨지는 구간(600~680)이 생긴다. 그래서 320 + 380 = 700 에서 끊는다.
 */
const S2_SPLIT_AT = "@container (min-width: 700px)";
/* 카드가 "좁다"의 기준 — 갈라지지 않은(=한 칸인) 카드에서 라벨+입력칸+안내를 한 줄에 못 세우는 폭. */
const CARD_NARROW = "@container (max-width: 400px)";

/*
 * 적용 중인 모델 한 행. 그리드로 칸을 고정한다 — 예전에는 flex 라서 "기본" 배지가 붙은 행만
 * 파라미터가 오른쪽으로 밀려서, 같은 뜻의 입력칸(창 크기 등)이 행마다 다른 자리에 있었다.
 * 배지 칸은 배지가 없어도 자리를 비워 둔다. 폭은 가장 긴 번역에 맞춘다 — 52px 이었을 때
 * 일본어("デフォルト")가 두 줄로 접혔다. 전각 5자 × --fs-body 최대값(Big=13px) + 좌우 padding
 */
/* 60em = Small 720px / Big 840px. **px 로 못 박지 않는다** — 한 줄에 드는 폭은 라벨 길이에 딸려
   있고 라벨은 폰트 토큰을 따라 커진다(Big 은 +2px, 약 18%). px 로 두면 Big 에서만 다시 잘린다.
   컨테이너 질의의 em 은 **질의 컨테이너 자신의 font-size** 기준이라, 그 상자에 FS.SUB 를 준다. */
const APPLIED_ROW_NARROW = "@container (max-width: 60em)";
const APPLIED_ROW_SX = {
  display: "grid",
  gridTemplateColumns: "20px 132px 78px minmax(0, 1fr) 32px",
  gridTemplateAreas: '"radio name badge param close"',
  alignItems: "center",
  columnGap: 1.25,
  px: 1.375,
  py: 0.875,
  [APPLIED_ROW_NARROW]: {
    /* 이름은 제 글자폭만 쓰고(minmax(0,auto)) 남는 폭은 배지 칸이 먹는다 — 1fr 을 이름에 주면
       배지가 줄 끝까지 밀려 나가 어느 모델의 "기본"인지 눈으로 못 잇는다. */
    gridTemplateColumns: "20px minmax(0, auto) minmax(0, 1fr) 32px",
    gridTemplateAreas: '"radio name badge close" ". param param param"',
    rowGap: 0.75,
  },
};

/*
 * 파라미터 칸도 그리드다. flex + minWidth 로는 어긋난다 — 체크박스가 있는 행은 MUI 가 라벨을
 * ml:-9px 만큼 당겨서, 그 행의 입력칸만 9px 왼쪽에 섰다(첫 칸의 "폭"이 아니라 "끝나는 자리"가
 * 행마다 달랐다). 칸 경계를 그리드로 못 박으면 안에서 무엇이 당겨지든 입력칸 자리는 고정된다.
 */
/*
 * ⚠ 앞자리 폭은 **px 가 아니라 em** 이다(타이포 표준 §5 — "텍스트 옆 요소를 고정 px 로 맞추지 말 것").
 *   예전엔 208 → 250px 로 두 번 손으로 늘렸는데, 그래도
 *     · 글자 크기 Big(+2px, 약 18%) 에서 다시 접히고,
 *     · 베트남어("Áp Dụng Hồi Quy Điểm Phân Vị")는 영어보다 길어 넓은 화면에서도 접혔다.
 *   em 으로 두면 FS.SUB 토큰이 바뀔 때 칸도 같이 늘어나 손볼 일이 없다.
 *   (그래서 이 박스에 fontSize 를 직접 준다 — em 의 기준이 필요하다.)
 * 두 칸 다 최댓값만 정하고 줄어들 수 있게 둔다: 좁아지면 넘쳐서 잘리는 대신 칸이 줄고,
 * 그래도 모자라면 아래 PARAM_STACK_AT 에서 입력칸이 라벨 밑으로 내려간다.
 */
const PARAM_LEAD_W = "21em";
const PARAM_FIELD_W = "12.5em";
const PARAM_FIELD_MIN_W = "6em";
/* 라벨과 입력칸을 한 줄에 못 세우는 폭. 여기서부터는 세로로 쌓아 라벨에 한 줄을 통째로 준다.
   여기도 em 이다(40em = Small 480px / Big 560px) — 이유는 APPLIED_ROW_NARROW 주석 참고. */
const PARAM_STACK_AT = "@container (max-width: 40em)";
const PARAM_ROW_SX = {
  display: "grid",
  fontSize: FS.SUB,
  gridTemplateColumns: `minmax(0, ${PARAM_LEAD_W}) minmax(${PARAM_FIELD_MIN_W}, ${PARAM_FIELD_W})`,
  alignItems: "center",
  columnGap: 1,
  minWidth: 0,
  [PARAM_STACK_AT]: {
    /* 라벨에 줄을 통째로 준다 — 여기서도 칸을 12.5em 로 묶으면 줄을 바꾼 보람 없이 그대로 말줄임된다.
       입력칸(마지막 자식)만 원래 폭으로 묶어 밑줄이 줄 끝까지 늘어나지 않게 한다. */
    gridTemplateColumns: "minmax(0, 1fr)",
    rowGap: 0.5,
    "& > :last-child": { maxWidth: PARAM_FIELD_W },
  },
};

/*
 * 파라미터 라벨 — **절대 접지 않는다.** 칸이 모자라면 말줄임으로 물러나고 툴팁이 전문을 준다.
 * lineHeight 1.5 는 필수다: overflow:hidden 인 채로 1.3 밑으로 조이면 한글·한자 자획 아래가
 * 잘린다(타이포 표준 §5).
 */
const PARAM_LABEL_SX = {
  minWidth: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: 1.5,
};

const FieldLabel = ({ children, disabled }) => (
  <Box sx={{ fontSize: FS.BODY, color: disabled ? "text.disabled" : "text.secondary", mb: 0.5 }}>
    {children}
  </Box>
);

const Note = ({ children, sx }) => (
  <Box sx={{ fontSize: FS.BODY, lineHeight: 1.6, color: "text.disabled", ...sx }}>{children}</Box>
);

/*
 * 브랜드 틴트 — 표 머리와 "고른 것"에 같은 계열을 쓴다.
 * 예전에는 rgba(217, 236, 253) 을 그 자리에 직접 적어 두어, 앱의 teal 계열과 어긋난 파랑이
 * 이 화면에서만 떴다. 색은 팔레트에서 끌어와 한 곳에서 정한다.
 */
const tintHead = theme => alpha(theme.palette.primary.main, 0.1);
const tintRow = theme => alpha(theme.palette.primary.main, 0.06);
const tintRowHover = theme => alpha(theme.palette.primary.main, 0.1);

/* 대상 조합 그리드의 축·머리 글자 — 축 이름(작고 흐리게) / 레벨 이름(값) / 그 줄의 켜진 수 */
const AXIS_CAPTION_SX = {
  fontSize: FS.CAPTION, fontWeight: 600, letterSpacing: "0.6px",
  textTransform: "uppercase", color: "text.disabled", lineHeight: 1.4,
};
const LEVEL_NAME_SX = { fontSize: FS.TITLE, fontWeight: 700, color: "text.primary", lineHeight: 1.4 };
const LINE_COUNT_SX = { fontSize: FS.CAPTION, color: "text.secondary", lineHeight: 1.4 };

/** 스텝 카드 한 장. 제목 줄 오른쪽의 요약은 접힘이 없어진 대신 "이 스텝의 현재 값"을 말한다. */
const StepCard = ({ id, num, title, summary, children }) => (
  <AppCard id={id} sx={{ mb: 1.5 }}>
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 2, px: 2, py: 1, borderBottom: 1, borderColor: "divider",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
        {/*
          * 스텝 번호는 알약으로 든다 — 회색 글자로 두면 제목에 묻혀 "몇 번째 단계인지"가
          * 훑을 때 안 잡힌다. 색은 브랜드 하나만 쓴다(단계마다 색을 달리하면 색이 순서가 아니라
          * 종류를 뜻하는 것처럼 읽힌다).
          */}
        <Box sx={{
          flex: "none", fontSize: FS.CAPTION, fontWeight: 700, letterSpacing: "0.4px",
          lineHeight: 1.6, px: 0.875, borderRadius: 999,
          color: "primary.main", bgcolor: tintHead,
        }}>{num}</Box>
        <Box sx={SECTION_TITLE_SX}>{title}</Box>
      </Box>
      <Box sx={{ fontSize: FS.BODY, color: "text.secondary", textAlign: "right" }}>{summary}</Box>
    </Box>
    {children}
  </AppCard>
);

const BfSettings = ({ t, viewName }) => {
  const { dpConfig, bfAlgorithm, buckets, bfMetric, module, SHOW_POS } = codeUtil;
  const { POS_ACTUAL_FLG } = dpConfig;
  const { snackBarMessageColor, algorithmColor, tintChipSx } = colorUtil;

  const reduxDispatch = useDispatch();

  const CURRENT_DATE = new ZDate();
  const START_DATE = addMonths(CURRENT_DATE, -36);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [bfParameters, setBfParameters] = useState(null);
  const [planBucket, setPlanBucket] = useState(buckets.MONTH);
  const [updatedTs, setUpdatedTs] = useState(null);

  /*
   * BF Source Data(POS 실적을 BF 학습에 반영할지) — z_plan_config(moduleCd=DP, configCd=POS_ACTUAL_FLG).
   * N-Months(numVal)는 DP Settings 화면 소관으로 남아 있다 — 여기서는 boolVal만 갖고 있다가
   * 저장 시 그대로(numVal 등은 손대지 않고) 다시 올려보낸다.
   */
  const [posActualFlg, setPosActualFlg] = useState(false);
  const posActualConfigRef = useRef(null);

  const [missingValue, setMissingValue] = useState(true);
  const [removeOutlier, setRemoveOutlier] = useState(false);
  const [sensitivity, setSensitivity] = useState(4);
  const [outlierMethod, setOutlierMethod] = useState("interpolate");
  const [metrics, setMetrics] = useState(bfMetric[0]);
  const [replaceOutlier, setReplaceOutlier] = useState(false);
  const [threshold, setThreshold] = useState(20);
  const [multiplier, setMultiplier] = useState(110);
  const [bfFlgNYoy, setBfFlgNYoy] = useState(true);
  const [zbaseStrategy, setZbaseStrategy] = useState("last");
  const [zbaseConstant, setZbaseConstant] = useState(50);
  const [zSmaWindowSize, setZSmaWindowSize] = useState(36);
  const [smaWindowSize, setSmaWindowSize] = useState(36);
  const [rfQuantile, setRfQuantile] = useState(0.95);
  const [xgbQuantile, setXgbQuantile] = useState(0.95);
  const [historyHorizon, setHistoryHorizon] = useState(1);
  const [algorithmQuantile, setAlgorithmQuantile] = useState({ rfQuantileFlg: false, xgbQuantileFlg: false });
  const [algorithmState, setAlgorithmState] = useState({
    PR: false, RF: false, CRST: false, XGB: false, ZBASE: false,
    ZSMA: false, SMA: false, AR: false, HW: false, ZTFM: false,
  });
  const { rfQuantileFlg, xgbQuantileFlg } = algorithmQuantile;

  const [trainingType, setTrainingType] = useState(TRAINING_TYPE.PERIOD);
  const [defaultAlgorithm, setDefaultAlgorithm] = useState(bfAlgorithm.PR);
  const [trainingPeriod, setTrainingPeriod] = useState(36);
  const [trainingFullData, setTrainingFullData] = useState(false);
  const [startDate, setStartDate] = useState(dateUtils.formatDate(dateUtils.startOfMonthDate(START_DATE), "yyyy-MM-dd"));
  const [targetHrchy, setTargetHrchy] = useState(INIT_HRCHY);
  const [instanceType, setInstanceType] = useState(null);

  const getRulesRef = useRef(() => []); // 자식이 등록
  const [initialRules, setInitialRules] = useState([]); // 거래처-품목 계층별 알고리즘 예측
  // 실체(실품목/실거래처) 정확 지정 룰 중 카드로 롤업 복원되지 못한 행 — 요약 표시 + 저장 시 보존
  const [uploadRules, setUploadRules] = useState([]);
  const [ruleCount, setRuleCount] = useState(0);
  // 저장 후 목록을 다시 읽은 횟수. 자식(BfAlgorithmSettings)은 initialRules 로 카드를 처음 한 번만 만들므로,
  // 저장 직후 저장 행(dbRows)이 붙은 카드로 갈아끼우려면 이 값으로 재구성을 알려야 한다(새로고침 불필요).
  const [rulesReloadKey, setRulesReloadKey] = useState(0);
  const [orphanRuleCount, setOrphanRuleCount] = useState(0);

  const [activeStep, setActiveStep] = useState(() => reduxUtil.getViewState(viewName)?.activeStep ?? STEP_IDS.S1);
  const paneRef = useRef(null);

  /* 저장 시점의 값. "변경 사항" 목록과 초기화 버튼의 기준선이다. */
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  /* 저장~재조회 구간 잠금. 안 잠그면 저장 직전 값이 draft 로 되살아나 서버 최신값을 덮는다. */
  const discardDraftRef = useRef(false);
  /* 되살린 규칙 카드 보호. loadAlgoRules 는 비동기라 복원보다 늦게 끝난다. */
  const rulesRestoredRef = useRef(false);
  const [needBaseline, setNeedBaseline] = useState(false);

  // z_bf_algo_rule 테이블에서 룰 로드. 실패(구버전 서버 등) 시 JSON 룰 폴백.
  // 화면 룰은 범위 그대로 저장돼 있다(설계 §3.2): 계층 노드 + leaf_scope 로 카드 1:1 복원.
  // 실체(ENTITY) 행(CSV 정확 지정·과거 전개 행)은 부모 계층 leaf 노드의 하위 전체를 커버하면
  // 그 노드로 롤업해 카드로 복원하고, 커버하지 못하면 요약행으로 둔다
  const loadAlgoRules = useCallback((fallbackJsonRules) => {
    return bfAlgoRuleService.list()
      .then(response => {
        const rows = response?.data ?? [];
        const cards = [];
        const leftovers = [];
        const entityRows = [];

        // npiFlg 는 DB/DTO 가 'Y'/'N' 문자열이다(BfAlgoRuleDto). 화면 룰은 npi 를 **boolean 으로만**
        // 판별하므로(typeof r.npi === "boolean") 여기서 반드시 변환한다 — 문자열로 흘리면 NPI 룰이
        // 일반 룰로 취급돼 카드에 품목 칩이 사라지고, 저장 시 npi:null 로 뭉개진다.
        const npiOf = (r) => (typeof r.npiFlg === "boolean" ? r.npiFlg : r.npiFlg === "Y");

        for (const r of rows) {
          const custIsNode = r.custNodeType === "HRCHY";
          const itemIsNode = r.itemNodeId == null || r.itemNodeType === "HRCHY";
          if (custIsNode && itemIsNode) {
            // 계층 노드 룰(범위 또는 노드 자체) → 카드 1:1.
            // 레벨: leaf_scope='Y' 는 Customer(1)/Item(2) 리프 레벨, 아니면 노드의 hrchyLv 로 복원(custLevelId 미지정).
            // NPI 룰은 품목 축이 없으므로 항상 Item 레벨.
            cards.push({
              custLevelId: r.customerLeafScope === "Y" ? 1 : undefined,
              custHrchyId: r.customerNodeId, custHrchyName: r.customerName,
              itemLevelId: (r.itemNodeId == null || r.itemLeafScope === "Y") ? 2 : undefined,
              itemHrchyId: r.itemNodeId ?? undefined, itemHrchyName: r.itemName,
              npi: r.itemNodeId == null ? npiOf(r) : undefined,
              models: (r.models ?? "").split(";").filter(Boolean), dbRows: [r] });
          } else {
            entityRows.push(r);
          }
        }

        // 실체 축은 부모 노드 단위로 그룹핑해 커버리지 판정
        const groups = new Map();
        for (const r of entityRows) {
          const custKey = r.custNodeType === "ENTITY" ? `P:${r.custParentNodeId}` : `N:${r.customerNodeId}`;
          const itemKey = r.itemNodeId == null ? `NPI:${r.npiFlg}`
            : r.itemNodeType === "ENTITY" ? `P:${r.itemParentNodeId}` : `N:${r.itemNodeId}`;
          const key = `${custKey}|${itemKey}|${r.models}`;
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(r);
        }
        for (const g of groups.values()) {
          const s = g[0];
          const custEntity = s.custNodeType === "ENTITY";
          const itemEntity = s.itemNodeType === "ENTITY";
          const custCnt = new Set(g.map(r => r.customerNodeId)).size;
          const itemCnt = new Set(g.map(r => r.itemNodeId)).size;
          const custOk = !custEntity ||
            (s.custParentNodeId != null && custCnt === Number(s.custParentChildCount));
          const itemOk = !itemEntity ||
            (s.itemParentNodeId != null && itemCnt === Number(s.itemParentChildCount));
          const gridOk = custCnt * itemCnt === g.length;
          if (custOk && itemOk && gridOk && s.itemNodeId == null) {
            // NPI 전개 행 — 부모 노드 x 신제품 카드(Customer 레벨)로 복원
            cards.push({
              custLevelId: 1,
              custHrchyId: s.custParentNodeId,
              custHrchyName: s.custParentName,
              itemLevelId: 2,
              npi: npiOf(s),
              models: (s.models ?? "").split(";").filter(Boolean),
              dbRows: g,
            });
          } else if (custOk && itemOk && gridOk) {
            // 이름을 id 와 함께 넘긴다 — 실체(ENTITY) 노드는 계층 목록(z_*_hrchy)에 없어서
            // 자식이 id 로 못 찾고 `String(id)` 를 이름 자리에 그대로 찍는다(칩에 18자리 숫자가 보이던 원인).
            // 레벨 판정은 축의 "적용 대상"을 따른다 — 실체이거나 계층 노드라도 범위가 하위 전체(leaf_scope='Y')면
            // 최하단 레벨이다. 노드 자신의 레벨로 복원하면(=undefined) 화면 분류가 규칙 관리 화면과 어긋나고,
            // 그 상태로 저장하면 leaf_scope 가 'N' 으로 굳어 적용 대상이 하위 전체에서 노드 자체로 바뀐다.
            cards.push({
              custLevelId: (custEntity || s.customerLeafScope === "Y") ? 1 : undefined,
              custHrchyId: custEntity ? s.custParentNodeId : s.customerNodeId,
              custHrchyName: custEntity ? s.custParentName : s.customerName,
              itemLevelId: (itemEntity || s.itemLeafScope === "Y") ? 2 : undefined,
              itemHrchyId: itemEntity ? s.itemParentNodeId : s.itemNodeId,
              itemHrchyName: itemEntity ? s.itemParentName : s.itemName,
              models: (s.models ?? "").split(";").filter(Boolean),
              dbRows: g,   // 전개 저장된 원본 행 — 카드 세부사항(리프 목록)용
            });
          } else {
            leftovers.push(...g);
          }
        }

        // 대상이 삭제된 고아 룰. 서버는 이름·레벨을 조인해 파생하므로(BfAlgoRuleMapper §listBody)
        // 노드가 지워지면 파생 컬럼이 전부 NULL 로 온다 — 화면에 보여줄 이름이 없고 예측에도 안 쓰인다.
        setOrphanRuleCount(rows.filter(r => r.custNodeType == null
          || (r.itemNodeId != null && r.itemNodeType == null)).length);
        setUploadRules(leftovers);
        // 탭 복귀로 되살린 편집분이 있으면 서버 목록으로 덮지 않는다
        if (!rulesRestoredRef.current) setInitialRules(cards);
      })
      .catch(e => {
        debug(e);
        setUploadRules([]);
        if (!rulesRestoredRef.current) setInitialRules(fallbackJsonRules ?? []);
      });
  }, []);

  /* ------------------------------ 조회 / 파싱 ------------------------------ */

  /** 스냅샷 → 상태. 탭 복귀 복원과 초기화 버튼이 함께 쓴다. */
  const restoreSnapshot = (s) => {
    if (!s) return;
    setTargetHrchy({ ...s.targetHrchy });
    setBfFlgNYoy(s.bfFlgNYoy);
    setRemoveOutlier(s.removeOutlier);
    setSensitivity(s.sensitivity);
    setOutlierMethod(s.outlierMethod);
    setTrainingType(s.trainingType);
    setTrainingPeriod(s.trainingPeriod);
    setStartDate(s.startDate);
    setTrainingFullData(s.trainingFullData);
    setAlgorithmState({ ...s.algorithmState });
    setDefaultAlgorithm(bfAlgorithm[s.defaultAlgorithmCd] ?? bfAlgorithm.PR);
    setAlgorithmQuantile({ rfQuantileFlg: s.rfQuantileFlg, xgbQuantileFlg: s.xgbQuantileFlg });
    setRfQuantile(s.rfQuantile);
    setXgbQuantile(s.xgbQuantile);
    setZbaseStrategy(s.zbaseStrategy);
    setZbaseConstant(s.zbaseConstant);
    setZSmaWindowSize(s.zSmaWindowSize);
    setSmaWindowSize(s.smaWindowSize);
    setReplaceOutlier(s.replaceOutlier);
    setThreshold(s.threshold);
    setMultiplier(s.multiplier);
    setHistoryHorizon(s.historyHorizon);
    setMetrics(s.metrics);
    setPosActualFlg(s.posActualFlg);
  };

  const setParameters = () => {
    if (codeUtil.isWeekBucket(planBucket)) {
      setSmaWindowSize(150);
      setZSmaWindowSize(150);
    } else {
      setSmaWindowSize(36);
      setZSmaWindowSize(36);
    }
    const parameters = bfParameters.objVal;
    if (parameters) {
      const defaultAlgo = parameters?.default ? bfAlgorithm[parameters?.default] : bfAlgorithm.PR;
      setDefaultAlgorithm(defaultAlgo);
      setRemoveOutlier(parameters.removeOutlier);
      setSensitivity(parameters.sensitivity);
      setOutlierMethod(parameters.outlierMethod);
      setMetrics(parameters.metrics);
      setReplaceOutlier(parameters.replaceOutlier);
      setThreshold(parameters.threshold);
      setMultiplier(parameters.multiplier);
      loadAlgoRules(parameters.fcstAlgoRulesId);
      setTargetHrchy(parameters?.targetHierarchy ?? INIT_HRCHY);
      setBfFlgNYoy(parameters?.bfFlgNYoy ?? true);
      if (parameters.bestSelection?.historyHorizon && parameters.bestSelection.historyHorizon > 0) {
        setHistoryHorizon(parameters.bestSelection.historyHorizon);
      }
      if (parameters.bestSelection?.metrics) {
        setMetrics(parameters.bestSelection.metrics);
      }

      if (parameters?.algorithm && Object.keys(parameters?.algorithm)) {
        const nextAlgoState = { ...algorithmState };
        const nextQuantile = { ...algorithmQuantile };
        for (const element of (Object.keys(parameters.algorithm))) {
          nextAlgoState[element] = true;
          if (element === bfAlgorithm.RF.cd) {
            setRfQuantile(parameters.algorithm[element].quantile);
            nextQuantile.rfQuantileFlg = parameters.algorithm[element].postQuantile ?? false;
          } else if (element === bfAlgorithm.XGB.cd) {
            setXgbQuantile(parameters.algorithm[element].quantile);
            nextQuantile.xgbQuantileFlg = parameters.algorithm[element].postQuantile ?? false;
          } else if (element === bfAlgorithm.ZBASE.cd) {
            setZbaseConstant(parameters.algorithm[element].constant);
            setZbaseStrategy(parameters.algorithm[element].strategy);
          } else if (element === bfAlgorithm.ZSMA.cd) {
            setZSmaWindowSize(parameters.algorithm[element].windowSize);
          } else if (element === bfAlgorithm.SMA.cd) {
            setSmaWindowSize(parameters.algorithm[element].windowSize);
          }
        }
        if (defaultAlgo?.cd) nextAlgoState[defaultAlgo.cd] = true;
        setAlgorithmState(nextAlgoState);
        setAlgorithmQuantile(nextQuantile);
      }
    }

    if (parameters?.trainingPeriod && Object.keys(parameters?.trainingPeriod)) {
      for (const element of (Object.keys(parameters.trainingPeriod))) {
        if (element === TRAINING_TYPE.PERIOD) {
          setTrainingPeriod(parameters.trainingPeriod[element]);
          setTrainingType(TRAINING_TYPE.PERIOD);
        } else if (element === TRAINING_TYPE.DATE) {
          setStartDate(parameters.trainingPeriod[element]);
          setTrainingType(TRAINING_TYPE.DATE);
        }
        if (element === TRAINING_TYPE.LAST_BUCKET_FULL_FLAG) {
          setTrainingFullData(parameters.trainingPeriod[element]);
        }
      }
    }
  };

  const retrieveParameters = () => {
    const param = { moduleCd: module.DP, activeFlg: true };
    planConfigService.getAll(param)
      .then(response => {
        if (response.data) {
          let bfParams = {};
          for (const result of response.data.results) {
            if (result.configCd === dpConfig.BF_PARAMETER) {
              bfParams = result;
              setUpdatedTs(result.updatedTs ?? null);
              if (result.objVal?.instanceType) {
                setInstanceType(result.objVal?.instanceType);
              }
            }
            else if (result.configCd === dpConfig.BUCKET_UNIT) {
              setPlanBucket(result.strVal);
            }
            else if (result.configCd === POS_ACTUAL_FLG) {
              posActualConfigRef.current = result;
              setPosActualFlg(result.boolVal ?? false);
            }
          }
          setBfParameters(bfParams);
        }
      })
      .catch(e => {
        discardDraftRef.current = false; // 재조회 실패 — 잠근 채 두면 이후 편집이 안 담긴다
        debug(e);
      });
  };

  /* ------------------------------ 변경 추적 ------------------------------ */

  /*
   * 저장 대상 값 전부를 한 덩어리로 뜬다. 저장 직후의 스냅샷과 지금 값을 비교해
   * "변경 사항" 목록을 만들고, 초기화는 이 스냅샷을 그대로 되돌려 심는다.
   */
  const snapshot = useMemo(() => ({
    targetHrchy: { ...targetHrchy },
    bfFlgNYoy, removeOutlier, sensitivity, outlierMethod,
    trainingType, trainingPeriod, startDate, trainingFullData,
    algorithmState: { ...algorithmState },
    defaultAlgorithmCd: defaultAlgorithm?.cd,
    rfQuantileFlg, rfQuantile, xgbQuantileFlg, xgbQuantile,
    zbaseStrategy, zbaseConstant, zSmaWindowSize, smaWindowSize,
    replaceOutlier, threshold, multiplier, historyHorizon, metrics,
    posActualFlg,
  }), [targetHrchy, bfFlgNYoy, removeOutlier, sensitivity, outlierMethod, trainingType,
    trainingPeriod, startDate, trainingFullData, algorithmState, defaultAlgorithm,
    rfQuantileFlg, rfQuantile, xgbQuantileFlg, xgbQuantile, zbaseStrategy, zbaseConstant,
    zSmaWindowSize, smaWindowSize, replaceOutlier, threshold, multiplier, historyHorizon, metrics,
    posActualFlg]);

  const enabledCodes = useMemo(
    () => Object.values(bfAlgorithm).sort((a, b) => a.seq - b.seq).filter(a => algorithmState[a.cd]).map(a => a.cd),
    [algorithmState, bfAlgorithm]);
  const availableAlgos = useMemo(
    () => Object.values(bfAlgorithm).sort((a, b) => a.seq - b.seq).filter(a => !algorithmState[a.cd]),
    [algorithmState, bfAlgorithm]);
  const appliedAlgos = useMemo(
    () => Object.values(bfAlgorithm).sort((a, b) => a.seq - b.seq).filter(a => algorithmState[a.cd]),
    [algorithmState, bfAlgorithm]);

  const checkedCells = useMemo(() => {
    const out = [];
    for (const c of LEVELS) for (const i of LEVELS) if (targetHrchy[hrchyKey(c, i)]) out.push({ c, i });
    return out;
  }, [targetHrchy]);

  const periodInvalid = trainingType === TRAINING_TYPE.PERIOD && (trainingPeriod < 12 || trainingPeriod > 120);

  const changes = useMemo(() => {
    if (!savedSnapshot) return [];
    const prev = savedSnapshot;
    const cur = snapshot;
    const out = [];
    const push = (step, label, delta) => out.push({ step, label, delta });
    const onOff = v => (v ? t("on") : t("off"));

    const prevChecked = Object.values(prev.targetHrchy).filter(Boolean).length;
    const curChecked = Object.values(cur.targetHrchy).filter(Boolean).length;
    if (prevChecked !== curChecked) push(STEP_IDS.S1, t("targetHierarchyLevel"), `${prevChecked} → ${curChecked}`);
    if (prev.bfFlgNYoy !== cur.bfFlgNYoy) push(STEP_IDS.S1, t("bfFlagNStrategy"), `${onOff(prev.bfFlgNYoy)} → ${onOff(cur.bfFlgNYoy)}`);

    if (prev.removeOutlier !== cur.removeOutlier) push(STEP_IDS.S2, t("removeOutlier"), `${onOff(prev.removeOutlier)} → ${onOff(cur.removeOutlier)}`);
    if (prev.sensitivity !== cur.sensitivity) push(STEP_IDS.S2, t("sensitivity"), `±${prev.sensitivity}σ → ±${cur.sensitivity}σ`);
    if (prev.outlierMethod !== cur.outlierMethod) push(STEP_IDS.S2, t("method"), `${t(prev.outlierMethod)} → ${t(cur.outlierMethod)}`);
    if (prev.trainingType !== cur.trainingType || prev.trainingPeriod !== cur.trainingPeriod || prev.startDate !== cur.startDate) {
      const label = s => (s.trainingType === TRAINING_TYPE.PERIOD ? `${s.trainingPeriod}${t("months")}` : s.startDate);
      push(STEP_IDS.S2, t("trainingPeriod"), `${label(prev)} → ${label(cur)}`);
    }
    if (prev.trainingFullData !== cur.trainingFullData) push(STEP_IDS.S2, t("trainingDataCleansing"), `${onOff(prev.trainingFullData)} → ${onOff(cur.trainingFullData)}`);
    if (prev.posActualFlg !== cur.posActualFlg) push(STEP_IDS.S2, t("bfSourceData"), `${onOff(prev.posActualFlg)} → ${onOff(cur.posActualFlg)}`);

    const prevAlgos = Object.keys(prev.algorithmState).filter(cd => prev.algorithmState[cd]);
    const curAlgos = Object.keys(cur.algorithmState).filter(cd => cur.algorithmState[cd]);
    const added = curAlgos.filter(cd => !prevAlgos.includes(cd));
    const removed = prevAlgos.filter(cd => !curAlgos.includes(cd));
    if (added.length || removed.length) {
      const bits = [];
      if (added.length) bits.push(`+${added.map(cd => bfAlgorithm[cd]?.name ?? cd).join(", ")}`);
      if (removed.length) bits.push(`−${removed.map(cd => bfAlgorithm[cd]?.name ?? cd).join(", ")}`);
      push(STEP_IDS.S3, t("algorithm"), bits.join(" · "));
    }
    if (prev.defaultAlgorithmCd !== cur.defaultAlgorithmCd) {
      push(STEP_IDS.S3, t("default"),
        `${bfAlgorithm[prev.defaultAlgorithmCd]?.name ?? "-"} → ${bfAlgorithm[cur.defaultAlgorithmCd]?.name ?? "-"}`);
    }
    if (prev.rfQuantileFlg !== cur.rfQuantileFlg) push(STEP_IDS.S3, `${bfAlgorithm.RF.name} ${t("quantile")}`, `${onOff(prev.rfQuantileFlg)} → ${onOff(cur.rfQuantileFlg)}`);
    if (prev.rfQuantile !== cur.rfQuantile) push(STEP_IDS.S3, `${bfAlgorithm.RF.name} ${t("quantile")}`, `${prev.rfQuantile} → ${cur.rfQuantile}`);
    if (prev.xgbQuantileFlg !== cur.xgbQuantileFlg) push(STEP_IDS.S3, `${bfAlgorithm.XGB.name} ${t("quantile")}`, `${onOff(prev.xgbQuantileFlg)} → ${onOff(cur.xgbQuantileFlg)}`);
    if (prev.xgbQuantile !== cur.xgbQuantile) push(STEP_IDS.S3, `${bfAlgorithm.XGB.name} ${t("quantile")}`, `${prev.xgbQuantile} → ${cur.xgbQuantile}`);
    if (prev.zbaseStrategy !== cur.zbaseStrategy) push(STEP_IDS.S3, `${bfAlgorithm.ZBASE.name} ${t("method")}`, `${t(prev.zbaseStrategy === "last" ? "lastValue" : prev.zbaseStrategy)} → ${t(cur.zbaseStrategy === "last" ? "lastValue" : cur.zbaseStrategy)}`);
    if (prev.zbaseConstant !== cur.zbaseConstant) push(STEP_IDS.S3, `${bfAlgorithm.ZBASE.name} ${t("constant")}`, `${prev.zbaseConstant} → ${cur.zbaseConstant}`);
    if (prev.zSmaWindowSize !== cur.zSmaWindowSize) push(STEP_IDS.S3, `${bfAlgorithm.ZSMA.name} ${t("windowSize")}`, `${prev.zSmaWindowSize} → ${cur.zSmaWindowSize}`);
    if (prev.smaWindowSize !== cur.smaWindowSize) push(STEP_IDS.S3, `${bfAlgorithm.SMA.name} ${t("windowSize")}`, `${prev.smaWindowSize} → ${cur.smaWindowSize}`);

    if (prev.replaceOutlier !== cur.replaceOutlier) push(STEP_IDS.S4, t("replaceOutlier"), `${onOff(prev.replaceOutlier)} → ${onOff(cur.replaceOutlier)}`);
    if (prev.threshold !== cur.threshold) push(STEP_IDS.S4, "Threshold pct", `${prev.threshold} → ${cur.threshold}`);
    if (prev.multiplier !== cur.multiplier) push(STEP_IDS.S4, "Multiplier pct", `${prev.multiplier} → ${cur.multiplier}`);

    if (prev.historyHorizon !== cur.historyHorizon) push(STEP_IDS.S5, t("historyHorizon"), `${prev.historyHorizon} → ${cur.historyHorizon}`);
    if (prev.metrics !== cur.metrics) push(STEP_IDS.S5, t("metrics"), `${prev.metrics} → ${cur.metrics}`);

    return out;
  }, [savedSnapshot, snapshot, bfAlgorithm, t]);

  const dirty = changes.length > 0;

  /*
   * 저장 버튼을 누르기 전에 읽어야 하는 것들. error 는 저장을 막고(첫 건은 본문 위 배너로도 뜬다),
   * warn 은 "이렇게 저장하면 이런 뜻이 된다"를 알릴 뿐 저장을 막지 않는다.
   */
  const issues = useMemo(() => {
    const out = [];
    if (checkedCells.length === 0) out.push({ level: "error", text: t("MSG_NoTargetHrchySelected") });
    if (periodInvalid) out.push({ level: "error", text: t("MSG_EnterIntBetween", { min: 12, max: 120 }) });
    if (enabledCodes.length <= 1) out.push({ level: "warn", text: t("MSG_SingleModelNoComparison") });
    if (!targetHrchy.CBIB && bfFlgNYoy) out.push({ level: "warn", text: t("MSG_YoyUnusedWithoutBottom") });
    if (replaceOutlier && threshold === 0) out.push({ level: "warn", text: t("MSG_ZeroThresholdReplacesAll") });
    if (orphanRuleCount > 0) out.push({ level: "warn", text: t("MSG_RuleTargetDeleted", { count: orphanRuleCount }) });
    return out;
  }, [checkedCells, periodInvalid, enabledCodes, targetHrchy.CBIB, bfFlgNYoy, replaceOutlier, threshold, orphanRuleCount, t]);

  const firstError = issues.find(w => w.level === "error");

  /* ------------------------------ 핸들러 ------------------------------ */

  const handleAlgorithmToggle = (cd, on) => {
    setAlgorithmState(prev => ({ ...prev, [cd]: on }));
  };

  const handleEnableAlgorithm = (cd) => handleAlgorithmToggle(cd, true);

  const handleDisableAlgorithm = (cd) => {
    if (cd === defaultAlgorithm?.cd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_CannotRemoveDefaultModel") });
      return;
    }
    handleAlgorithmToggle(cd, false);
  };

  const handleDefaultAlgorithm = (cd) => {
    setDefaultAlgorithm(bfAlgorithm[cd]);
    setAlgorithmState(prev => (prev[cd] ? prev : { ...prev, [cd]: true }));
  };

  const handleQuantileChange = (event) => {
    setAlgorithmQuantile({ ...algorithmQuantile, [event.target.name]: event.target.checked });
  };

  const handleToggleCell = (c, i) => {
    const key = hrchyKey(c, i);
    setTargetHrchy(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleToggleAllCells = (checked) => {
    const next = {};
    for (const c of LEVELS) for (const i of LEVELS) next[hrchyKey(c, i)] = checked;
    setTargetHrchy(next);
  };

  /*
   * 행(거래처 레벨 하나) / 열(품목 레벨 하나) 통째 토글.
   * 한 줄이 다 켜져 있으면 끄고, 아니면 다 켠다 — 머리를 누를 때마다 "전체 켜기 → 전체 끄기"로
   * 오가는 게 부분 선택 상태에서 무엇이 일어날지 가장 짐작하기 쉽다(표 머리 전체선택과 같은 규칙).
   */
  const toggleLine = (levels, keyOf) => {
    const allOn = levels.every(lv => targetHrchy[keyOf(lv)]);
    setTargetHrchy(prev => {
      const next = { ...prev };
      for (const lv of levels) next[keyOf(lv)] = !allOn;
      return next;
    });
  };

  const handleToggleCustRow = (c) => toggleLine(LEVELS, i => hrchyKey(c, i));
  const handleToggleItemCol = (i) => toggleLine(LEVELS, c => hrchyKey(c, i));

  /* 그 줄에서 몇 개가 켜졌는지 — 행·열 머리에 n/3 으로 단다 */
  const rowOnCount = (c) => LEVELS.filter(i => targetHrchy[hrchyKey(c, i)]).length;
  const colOnCount = (i) => LEVELS.filter(c => targetHrchy[hrchyKey(c, i)]).length;

  /*
   * 즉시 이동이다(behavior:"smooth" 아님). 스크롤 중 onScroll → setState → 리렌더가
   * 부드러운 스크롤 애니메이션을 중간에 끊어서, 누른 스텝이 아니라 어중간한 위치에 멈춘다.
   */
  const handleNavClick = (id) => {
    const pane = paneRef.current;
    const section = document.getElementById(id);
    if (pane && section) pane.scrollTop = Math.max(0, section.offsetTop - 16);
    setActiveStep(id);
  };

  const handlePaneScroll = () => {
    const pane = paneRef.current;
    if (!pane) return;
    const top = pane.scrollTop + 24;
    let next = STEP_IDS.S1;
    for (const id of Object.values(STEP_IDS)) {
      const el = document.getElementById(id);
      if (el && el.offsetTop <= top) next = id;
    }
    setActiveStep(prev => (prev === next ? prev : next));
  };

  const handleSave = () => {
    const allTargetHrchyFalse = Object.values(targetHrchy).every(value => value === false);
    // 카드 룰 → 스코프형 페이로드 (리프 대상 레벨은 서버가 하위 리프로 전개해 z_bf_algo_rule 저장)
    const scopedRules = getRulesRef.current().map(r => ({
      custHrchyId: r.custHrchy?.id ?? null,
      custLeafScope: r.custLevel?.cd === codeUtil.customer.cd,
      itemHrchyId: r.itemHrchy?.id ?? null,
      itemLeafScope: r.itemLevel?.cd === codeUtil.item.cd,
      npi: typeof r.npi === "boolean" ? r.npi : null,
      models: r?.models ?? [],
    }));
    if (periodInvalid) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterIntBetween", { min: 12, max: 120 }) });
      return;
    }
    const buildAlgoEntry = (algo) => {
      if (algo === bfAlgorithm.RF.cd) {
        return { postQuantile: rfQuantileFlg, quantile: rfQuantile };
      } else if (algo === bfAlgorithm.XGB.cd) {
        return { postQuantile: xgbQuantileFlg, quantile: xgbQuantile };
      } else if (algo === bfAlgorithm.ZBASE.cd) {
        return { strategy: zbaseStrategy, constant: zbaseConstant };
      } else if (algo === bfAlgorithm.ZSMA.cd) {
        return { windowSize: zSmaWindowSize };
      } else if (algo === bfAlgorithm.SMA.cd) {
        return { windowSize: smaWindowSize };
      }
      return {};
    };
    let algoList = {};
    for (const algo of Object.keys(algorithmState)) {
      if (algorithmState[algo] === true) {
        algoList = { ...algoList, [algo]: buildAlgoEntry(algo) };
      }
    }
    // default 알고리즘은 체크 상태와 무관하게 항상 포함하여 저장
    if (defaultAlgorithm?.cd && !algoList[defaultAlgorithm.cd]) {
      algoList = { ...algoList, [defaultAlgorithm.cd]: buildAlgoEntry(defaultAlgorithm.cd) };
    }

    const periodObj = trainingType === TRAINING_TYPE.PERIOD
      ? { period: trainingPeriod, [TRAINING_TYPE.LAST_BUCKET_FULL_FLAG]: trainingFullData }
      : { date: startDate, [TRAINING_TYPE.LAST_BUCKET_FULL_FLAG]: trainingFullData };

    const objVal = {
      default: defaultAlgorithm?.cd,
      missingValue,
      removeOutlier,
      sensitivity,
      outlierMethod,
      algorithm: algoList,
      trainingPeriod: periodObj,
      replaceOutlier,
      threshold,
      multiplier,
      bestSelection: { historyHorizon, metrics },
      targetHierarchy: allTargetHrchyFalse === true ? INIT_HRCHY : targetHrchy,
      instanceType,
      // fcstAlgoRulesId 는 z_bf_algo_rule 테이블로 이관 — JSON(plan_config/plan_version 복사본)
      // 비대의 원인이라 더 이상 objVal 에 저장하지 않는다
      bfFlgNYoy,
    };

    // 룰 저장: 카드 룰(전개) + 업로드 유래 리프 룰(보존) 전체 교체.
    // 다시 읽은 뒤 자식 카드를 재구성해 저장 행 기준 건수가 바로 보이게 한다.
    bfAlgoRuleService.replaceScoped(scopedRules, uploadRules)
      .then(() => loadAlgoRules())
      .then(() => setRulesReloadKey((k) => k + 1))
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      });

    const onSaved = () => {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      // 저장 후엔 서버 최신값을 받아야 한다 — 편집 중 사본을 버리고 재조회까지 잠근다
      discardDraftRef.current = true;
      rulesRestoredRef.current = false;
      if (viewName) reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, { draft: null })));
      retrieveParameters();
    };
    const onFailed = (e) => {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
      discardDraftRef.current = false; // 재조회가 없으니 여기서 푼다
      debug(e);
    };

    if (bfParameters && Object.keys(bfParameters).length > 0) {
      bfParameters.objVal = objVal;
      planConfigService.upsert([{ ...bfParameters }]).then(onSaved).catch(onFailed);
    } else {
      const param = { moduleCd: module.DP, configCd: dpConfig.BF_PARAMETER, activeFlg: true, objVal };
      planConfigService.upsert([param]).then(onSaved).catch(onFailed);
    }

    // BF Source Data — N-Months(numVal)는 DP Settings 소관이라 손대지 않고, 로드해둔 행에 boolVal만 덮어 저장한다.
    // v26.10: POS 미출시 — 토글이 안 보이므로 저장도 하지 않는다(code-util SHOW_POS)
    if (!SHOW_POS) return;
    const posActualConfig = posActualConfigRef.current
      ?? { moduleCd: module.DP, configCd: POS_ACTUAL_FLG, activeFlg: true };
    planConfigService.upsert([{ ...posActualConfig, boolVal: posActualFlg }])
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      });
  };

  /** 저장 이후 바꾼 것을 전부 되돌린다 — 기준선은 마지막 저장 스냅샷이다. */
  const handleReset = () => {
    if (!savedSnapshot || !dirty) return;
    restoreSnapshot(savedSnapshot);
    setSnackInfo({ open: true, severity: snackBarMessageColor.confirm, content: t("MSG_ResetToSaved") });
  };

  const handleDialogOpen = () => {
    const allTargetHrchyFalse = Object.values(targetHrchy).every(value => value === false);
    if (allTargetHrchyFalse) {
      setDialogInfo({ open: true, title: "save", content: t("MSG_SaveNoTargetHrchyConfirm") });
    } else {
      handleSave();
    }
  };

  const handleDialog = (action) => {
    if (action === DIALOG_TITLE.SAVE) handleSave();
  };

  const handleRuleCountChange = useCallback((count) => setRuleCount(count), []);

  /* ------------------------------ 라이프사이클 ------------------------------ */

  useEffect(() => {
    retrieveParameters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (bfParameters === null) return;
    if (Object.keys(bfParameters).length > 0 && bfParameters.objVal) {
      setParameters();
    } else if (defaultAlgorithm?.cd) {
      setAlgorithmState(prev => ({ ...prev, [defaultAlgorithm.cd]: true }));
    }
    // 값이 다 심어진 다음 렌더에서 기준선을 뜬다 — 여기서 바로 뜨면 이전 값이 잡힌다
    setNeedBaseline(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bfParameters]);

  useEffect(() => {
    if (!needBaseline) return;
    // 기준선은 언제나 서버 저장값 — 되살린 편집값이 기준선이 되면 변경 비교가 어긋난다
    setSavedSnapshot(snapshot);
    setNeedBaseline(false);
    if (discardDraftRef.current) {
      discardDraftRef.current = false; // 저장 직후 재조회 — 버린 사본을 되살리지 않는다
      return;
    }
    // 탭 복귀 — 서버값 위에 편집 중이던 값을 덮어쓴다(탭을 닫으면 App.js 가 viewState 를 지운다)
    const vs = reduxUtil.getViewState(viewName);
    restoreSnapshot(vs?.draft);
    if (vs?.draftRules) {
      rulesRestoredRef.current = true;
      setInitialRules(vs.draftRules);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needBaseline, snapshot]);

  /* 편집값을 탭 상태에 보관 — 규칙 카드는 서버에서 읽는 값이라 draft 에 같이 담아야 산다. */
  useEffect(() => {
    if (!viewName || needBaseline || savedSnapshot === null) return;
    if (discardDraftRef.current) return;
    const rules = getRulesRef.current();
    // 복귀 직후 자식이 시드되기 전의 빈 배열은 "아직 준비 안 됨" — 되살린 카드를 지우면 안 된다
    const rulesNotReady = rulesRestoredRef.current && rules.length === 0 && initialRules.length > 0;
    const next = { draft: snapshot, activeStep };
    if (!rulesNotReady) next.draftRules = rules;
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, next)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, activeStep, ruleCount, savedSnapshot, needBaseline, viewName]);

  useEffect(() => {
    if (threshold < 0 || multiplier < 0 || historyHorizon < 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotEnterNegativeValue") });
      if (threshold < 0) setThreshold(0);
      if (multiplier < 0) setMultiplier(0);
      if (historyHorizon < 0) setHistoryHorizon(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threshold, multiplier, historyHorizon]);

  /* ------------------------------ 렌더 조각 ------------------------------ */

  const stepSummary = {
    [STEP_IDS.S1]: `${checkedCells.length} / 9${targetHrchy.CBIB && bfFlgNYoy ? ` · ${t("bfFlagNStrategy")}` : ""}`,
    [STEP_IDS.S2]: `${removeOutlier ? `${t("removeOutlier")} ±${sensitivity}σ` : `${t("removeOutlier")} ${t("off")}`} · ${trainingType === TRAINING_TYPE.PERIOD ? `${trainingPeriod}${t("months")}` : startDate}`,
    [STEP_IDS.S3]: `${enabledCodes.length} / ${Object.keys(bfAlgorithm).length} · ${t("default")} ${defaultAlgorithm?.name ?? "-"}`,
    [STEP_IDS.S4]: replaceOutlier ? `${t("on")} · ${threshold}% / ${multiplier}%` : t("off"),
    [STEP_IDS.S5]: `${metrics} · ${t("historyHorizon")} ${historyHorizon}`,
  };

  const navItems = [
    { id: STEP_IDS.S1, num: "01", label: t("targetHierarchyLevel") },
    { id: STEP_IDS.S2, num: "02", label: t("preprocessing") },
    { id: STEP_IDS.S3, num: "03", label: t("forecasting") },
    { id: STEP_IDS.S4, num: "04", label: t("postprocessing") },
    { id: STEP_IDS.S5, num: "05", label: t("modelSelection") },
  ];
  const changedSteps = new Set(changes.map(c => c.step));

  const allChecked = checkedCells.length === 9;

  const usedCust = new Set(checkedCells.map(x => x.c));
  const usedItem = new Set(checkedCells.map(x => x.i));
  // 요약 칸의 연결 다이어그램. 알약 높이 40 + 간격 10 → 중심이 20 / 70 / 120 에 온다.
  const linkPaths = checkedCells.map(({ c, i }) => {
    const sy = 20 + LEVELS.indexOf(c) * 50;
    const ey = 20 + LEVELS.indexOf(i) * 50;
    return { key: `${c}-${i}`, d: sy === ey ? `M118 ${sy} L182 ${sy}` : `M118 ${sy} C131 ${sy}, 169 ${ey}, 182 ${ey}` };
  });

  const bucketLabel = planBucket ? t(planBucket) : "";
  const lastSavedLabel = updatedTs ? dateUtils.formatDate(new ZDate(updatedTs), "yyyy-MM-dd HH:mm") : "-";

  const levelPill = (lvl, used) => (
    <Box key={lvl} sx={{
      height: 40, borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: FS.SUB, fontWeight: used ? 500 : 400,
      color: used ? "text.primary" : "text.disabled",
      bgcolor: used ? tintHead : "grey.100",
    }}>{t(lvl)}</Box>
  );

  const paramCell = (algo) => {
    const kind = ALGO_PARAM[algo.cd];
    if (!kind) return <Note>{t("noParameter")}</Note>;

    if (kind === "quantile") {
      const isRf = algo.cd === bfAlgorithm.RF.cd;
      const flgName = isRf ? "rfQuantileFlg" : "xgbQuantileFlg";
      const flg = isRf ? rfQuantileFlg : xgbQuantileFlg;
      const value = isRf ? rfQuantile : xgbQuantile;
      const setValue = isRf ? setRfQuantile : setXgbQuantile;
      return (
        <Box sx={PARAM_ROW_SX}>
          {/* 라벨이 말줄임으로 물러날 수 있으니 전문은 툴팁이 받는다 */}
          <Tooltip title={t("applyQuantileRegression")}>
            <FormControlLabel
              /* 스위치는 체크박스와 달리 안쪽 여백이 없다 — 라벨 시작선을 맞추던 ml:-9px 는 걷는다 */
              sx={{ mx: 0, minWidth: 0, "& .MuiFormControlLabel-label": { fontSize: FS.SUB, ...PARAM_LABEL_SX } }}
              control={<StyledSwitch sx={{ mr: 2, flex: "none" }} checked={flg} name={flgName} onChange={handleQuantileChange} />}
              label={t("applyQuantileRegression")}
            />
          </Tooltip>
          <TextField
            variant="standard" size="small" type="number" fullWidth value={value} disabled={!flg} sx={FIELD_SX}
            inputProps={{ inputMode: "decimal", min: 0, max: 1, step: "0.01" }}
            onChange={(e) => setValue(Math.min(1, Math.max(0, Number(e.target.value))))}
          />
        </Box>
      );
    }

    if (kind === "window") {
      const isSma = algo.cd === bfAlgorithm.SMA.cd;
      const value = isSma ? smaWindowSize : zSmaWindowSize;
      const setValue = isSma ? setSmaWindowSize : setZSmaWindowSize;
      return (
        <Box sx={PARAM_ROW_SX}>
          <Tooltip title={t("windowSize")}>
            <Box sx={{ fontSize: FS.SUB, color: "text.secondary", ...PARAM_LABEL_SX }}>{t("windowSize")}</Box>
          </Tooltip>
          <TextField
            variant="standard" size="small" type="number" fullWidth value={value} sx={FIELD_SX}
            inputProps={{ inputMode: "numeric", min: 1 }}
            onChange={(e) => setValue(Math.max(1, Number(e.target.value)))}
          />
        </Box>
      );
    }

    return (
      <Box sx={PARAM_ROW_SX}>
        <Select
          variant="standard" size="small" fullWidth value={zbaseStrategy} sx={FIELD_SX}
          onChange={(e) => setZbaseStrategy(e.target.value)}
        >
          <MenuItem value="last" sx={{ fontSize: FS.SUB }}>{t("lastValue")}</MenuItem>
          <MenuItem value="median" sx={{ fontSize: FS.SUB }}>{t("median")}</MenuItem>
          <MenuItem value="constant" sx={{ fontSize: FS.SUB }}>{t("constant")}</MenuItem>
        </Select>
        {/* 라벨을 칸 밖에 두면 이 행만 입력칸이 오른쪽으로 밀린다 — 안쪽 접두사로 넣어 시작선을 맞춘다 */}
        <TextField
          variant="standard"
          size="small" type="number" fullWidth value={zbaseConstant} disabled={zbaseStrategy !== "constant"}
          sx={FIELD_SX} inputProps={{ inputMode: "numeric" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ "& p": { fontSize: FS.BODY } }}>{t("constant")}</InputAdornment>
            ),
          }}
          onChange={(e) => setZbaseConstant(Number(e.target.value))}
        />
      </Box>
    );
  };

  /* ------------------------------ UI ------------------------------ */

  return (
    <Box sx={{ mt: 1, display: "flex", height: "calc(100vh - 125px)", width: "100%" }}>

      {/* ── 스텝 내비 ─────────────────────────────────────────────── */}
      <Box sx={{
        width: 220, flex: "none", display: "flex", flexDirection: "column",
        bgcolor: "background.paper", borderRight: 1, borderColor: "divider",
      }}>
        <Box sx={{ px: 2, pt: 1.75, pb: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Box sx={SECTION_TITLE_SX}>{t("menuBfSettings")}</Box>
          <Box sx={{ fontSize: FS.BODY, color: "text.disabled", mt: 0.5 }}>
            {[module.DP, t("settings"), bucketLabel].filter(Boolean).join(" · ")}
          </Box>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", py: 0.875 }}>
          {navItems.map(n => {
            const on = activeStep === n.id;
            return (
              <Box
                key={n.id}
                onClick={() => handleNavClick(n.id)}
                sx={{
                  display: "flex", gap: 1.125, alignItems: "flex-start", px: 1.5, py: 1.125, cursor: "pointer",
                  borderLeft: 2, borderColor: on ? "primary.main" : "transparent",
                  bgcolor: on ? theme => alpha(theme.palette.primary.main, 0.08) : "transparent",
                  "&:hover": { bgcolor: theme => alpha(theme.palette.primary.main, on ? 0.12 : 0.04) },
                }}
              >
                <Box sx={{ fontSize: FS.BODY, fontWeight: 700, flex: "none", pt: "1px", color: on ? "primary.main" : "text.disabled" }}>
                  {n.num}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ fontSize: FS.TITLE, fontWeight: 600, color: "text.primary" }}>{n.label}</Box>
                  <Box sx={{ fontSize: FS.BODY, lineHeight: 1.5, color: "text.secondary", mt: "2px" }}>
                    {stepSummary[n.id]}
                  </Box>
                </Box>
                {changedSteps.has(n.id) && (
                  <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "warning.main", mt: "5px", flex: "none" }} />
                )}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ px: 1.75, py: 1.375, borderTop: 1, borderColor: "divider", fontSize: FS.BODY, lineHeight: 1.6, color: "text.disabled" }}>
          {t("lastSaved")}<br />{lastSavedLabel}
        </Box>
      </Box>

      {/* ── 본문 ─────────────────────────────────────────────────── */}
      <Box
        ref={paneRef}
        onScroll={handlePaneScroll}
        sx={{ flex: 1, minWidth: 0, position: "relative", overflow: "auto", p: 2, pb: 7.5 }}
      >
        {/*
          읽기 폭 상한. 카드를 화면 끝까지 늘리면 초광폭 모니터에서 STEP 01·02·04·05 처럼
          컨트롤이 몇 개뿐인 카드가 왼쪽 1/3 만 쓰고 나머지가 빈칸으로 남는다.
          (스크롤 스파이의 offsetTop 은 position:relative 인 바깥 pane 기준이라 이 래퍼가 끼어도 그대로다.)
        */}
        {/* containerType — 각 STEP 카드가 **뷰포트가 아니라 이 읽기 폭**으로 배치를 정한다.
            좌우 고정 패널(220+340)에 눌려 뷰포트 1000px 대에서도 이 폭은 300px 아래로 내려간다. */}
        <Box sx={{ maxWidth: 1180, containerType: "inline-size" }}>
        {firstError && (
          <Box sx={{
            display: "flex", alignItems: "flex-start", gap: 1.125, px: 1.75, py: 1.25, mb: 1.75,
            borderRadius: 1, border: 1, borderColor: theme => alpha(theme.palette.error.main, 0.3),
            bgcolor: theme => alpha(theme.palette.error.main, 0.08), color: "error.dark",
          }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor", mt: "5px", flex: "none" }} />
            <Box sx={{ fontSize: FS.SUB, lineHeight: 1.5 }}>{firstError.text}</Box>
          </Box>
        )}

        {/* STEP 1 — 대상 계층 레벨 */}
        <StepCard
          id={STEP_IDS.S1}
          num={`${t("stepTitle")} 01`}
          title={t("targetHierarchyLevel")}
          summary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {/* 전체 선택은 링크로 낸다 — 표 안 어디에도 "모두" 자리가 없어졌고, 이 자리가 카드 전체를 뜻한다 */}
              <Box component="span" onClick={() => handleToggleAllCells(!allChecked)}
                sx={{ cursor: "pointer", color: "primary.main", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>
                {t("selectAll")}
              </Box>
              <Box component="span">{`${checkedCells.length} / 9 ${t("forecasted")}`}</Box>
            </Box>
          }
        >
          {/*
            * 고르는 값은 거래처 레벨 × 품목 레벨의 3×3 조합이다 — 조합 하나를 타일 하나로 두고
            * **그 타일에 조합 이름을 적는다**("Top × Middle"). 체크박스만 늘어놓으면 어느 칸이 무슨 조합인지
            * 행·열 머리를 두 번 짚어야 알 수 있는데, 이름을 적어 두면 칸 하나만 봐도 뜻이 닫힌다.
            * 행·열 머리에는 그 줄에서 몇 개가 켜졌는지(n/3)를 달아 둔다.
            */}
          <Box sx={{ p: 2 }}>
            {/*
              overflowX — 타일 하나에 "Top × Middle" 한 줄과 체크 표시가 들어가야 해서 150px 밑으로는
              줄일 수가 없다(줄이면 조합 이름이 말줄임돼 이 표의 존재 이유가 사라진다 — 위 주석).
              세 칸이면 최소 ~500px 이라 좁은 창에서는 어떤 수를 써도 안 들어간다. 그럴 때
              **이 상자 안에서만** 가로로 밀리게 한다: 이게 없으면 넘친 폭이 본문 전체로 번져
              화면 바닥에 가로 스크롤바가 생기고 STEP 02~05 카드까지 같이 옆으로 끌려 나간다.
            */}
            <Box sx={{ overflowX: "auto" }}>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: `auto repeat(${LEVELS.length}, minmax(150px, 1fr))`,
              columnGap: 1.25, rowGap: 1.25, alignItems: "center",
            }}>
              {/* 좌상단 — 세로축이 거래처임을 밝힌다 */}
              <Box sx={{ ...AXIS_CAPTION_SX, alignSelf: "end", pb: 0.5 }}>
                {t("customer")}
                <Box component="span" sx={{ display: "block", lineHeight: 1 }}>↓</Box>
              </Box>

              {/* 열 머리 — 품목 레벨. 누르면 그 열 전체가 켜지고 꺼진다 */}
              {LEVELS.map(i => (
                <Box key={`col-${i}`} onClick={() => handleToggleItemCol(i)}
                  sx={{ textAlign: "center", cursor: "pointer", "&:hover .lvl": { color: "primary.main" } }}>
                  <Box sx={AXIS_CAPTION_SX}>{t("item")}</Box>
                  <Box className="lvl" sx={LEVEL_NAME_SX}>{t(i)}</Box>
                  <Box sx={LINE_COUNT_SX}>{`${colOnCount(i)}/${LEVELS.length}`}</Box>
                </Box>
              ))}

              {LEVELS.map(c => (
                <Fragment key={`row-${c}`}>
                  {/* 행 머리 — 거래처 레벨. 누르면 그 행 전체가 켜지고 꺼진다 */}
                  <Box onClick={() => handleToggleCustRow(c)}
                    sx={{ pr: 1.5, cursor: "pointer", "&:hover .lvl": { color: "primary.main" } }}>
                    <Box className="lvl" sx={LEVEL_NAME_SX}>{t(c)}</Box>
                    <Box sx={LINE_COUNT_SX}>{`${rowOnCount(c)}/${LEVELS.length}`}</Box>
                  </Box>
                  {LEVELS.map(i => {
                    const key = hrchyKey(c, i);
                    const on = Boolean(targetHrchy[key]);
                    return (
                      <Box
                        key={key}
                        onClick={() => handleToggleCell(c, i)}
                        sx={{
                          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
                          px: 1.5, py: 1.375, borderRadius: 1.5, cursor: "pointer",
                          fontSize: FS.SUB, fontWeight: on ? 600 : 400,
                          border: 1,
                          borderColor: on ? theme => alpha(theme.palette.primary.main, 0.45) : "transparent",
                          bgcolor: on ? tintRow : "grey.50",
                          color: on ? "text.primary" : "text.disabled",
                          transition: "background .15s ease, border-color .15s ease",
                          "&:hover": {
                            bgcolor: on ? tintRowHover : "action.hover",
                            borderColor: on ? "primary.main" : theme => alpha(theme.palette.primary.main, 0.25),
                          },
                        }}
                      >
                        <Box sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {`${t(c)} × ${t(i)}`}
                        </Box>
                        {/* 켜짐은 찬 원, 꺼짐은 빈 원 — 같은 자리에서 색만 바뀌므로 줄을 따라 훑기 좋다 */}
                        {on
                          ? <CheckCircleIcon sx={{ fontSize: 20, color: "primary.main", flexShrink: 0 }} />
                          : <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: "grey.300", flexShrink: 0 }} />}
                      </Box>
                    );
                  })}
                </Fragment>
              ))}
            </Box>
            </Box>

            <Note sx={{ mt: 2 }}>{t("MSG_TargetHrchyNote")}</Note>

            {/* 최하단 × 최하단에만 걸리는 설정이라 그 조합이 켜졌을 때만 낸다 */}
            {targetHrchy.CBIB && (
              <Box sx={{
                display: "flex", alignItems: "center", gap: 1.5, mt: 1.5, pt: 1.5,
                borderTop: 1, borderColor: "divider",
              }}>
                <StyledSwitch checked={bfFlgNYoy} onChange={(e) => setBfFlgNYoy(e.target.checked)} />
                <Box sx={{ fontSize: FS.SUB, fontWeight: 500 }}>{t("bfFlagNStrategy")}</Box>
              </Box>
            )}
          </Box>
        </StepCard>

        {/* STEP 2 — 전처리 */}
        <StepCard
          id={STEP_IDS.S2}
          num={`${t("stepTitle")} 02`}
          title={t("preprocessing")}
          summary={stepSummary[STEP_IDS.S2]}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", [S2_SPLIT_AT]: { gridTemplateColumns: "minmax(0, 320px) minmax(0, 1fr)" } }}>
            <Box sx={{ p: 1.5, borderRight: 0, [S2_SPLIT_AT]: { borderRight: 1 }, borderBottom: 1, borderColor: "divider" }}>
              <Box sx={{ ...SUB_HEADER_SX, mb: 1.375 }}>{t("missingValue")}</Box>
              <Tooltip title={t("MSG_Substitute")}>
                <FormControlLabel
                  disabled
                  sx={{ ml: "1px", "& .MuiFormControlLabel-label": { fontSize: FS.SUB } }}
                  control={<StyledSwitch sx={{ mr: 2 }} checked={missingValue} onChange={(e) => setMissingValue(e.target.checked)} />}
                  label={t("processMissingValue")}
                />
              </Tooltip>
              <Note sx={{ mt: 1 }}>{t("MSG_Substitute")}</Note>
            </Box>

            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
              <Box sx={{ ...SUB_HEADER_SX, mb: 1.375 }}>{t("outlier")}</Box>
              <FormControlLabel
                sx={{ ml: "1px", mb: 1, "& .MuiFormControlLabel-label": { fontSize: FS.SUB } }}
                control={<StyledSwitch sx={{ mr: 2 }} checked={removeOutlier} onChange={(e) => setRemoveOutlier(e.target.checked)} />}
                label={t("removeOutlier")}
              />
              <Box sx={{ display: "flex", gap: 1.125, maxWidth: 340 }}>
                <Box sx={{ width: 120 }}>
                  <FieldLabel disabled={!removeOutlier}>{t("sensitivity")}</FieldLabel>
                  <TextField
                    variant="standard" size="small" type="number" value={sensitivity} disabled={!removeOutlier} fullWidth sx={FIELD_SX}
                    inputProps={{ inputMode: "decimal", min: 0, max: 10, step: "any", style: { textAlign: "center" } }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start" sx={{ "& p": { fontSize: FS.SUB } }}>±</InputAdornment>,
                      endAdornment: <InputAdornment position="end" sx={{ "& p": { fontSize: FS.SUB } }}>σ</InputAdornment>,
                    }}
                    onChange={(event) => {
                      const inputValue = Number(event.target.value);
                      if (isNaN(inputValue)) return;
                      setSensitivity(parseFloat(Math.min(10, Math.max(0, inputValue)).toFixed(2)));
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FieldLabel disabled={!removeOutlier}>{t("method")}</FieldLabel>
                  <Select
                    variant="standard" size="small" fullWidth value={outlierMethod} disabled={!removeOutlier} sx={FIELD_SX}
                    onChange={(event) => setOutlierMethod(event.target.value)}
                  >
                    <MenuItem value="interpolate" sx={{ fontSize: FS.SUB }}>{t("interpolate")}</MenuItem>
                    <MenuItem value="ffill" sx={{ fontSize: FS.SUB }}>{t("ffill")}</MenuItem>
                    <MenuItem value="bfill" sx={{ fontSize: FS.SUB }}>{t("bfill")}</MenuItem>
                  </Select>
                </Box>
              </Box>
            </Box>

            {/* BF Source Data — DP Settings에서 옮겨온 토글. N-Months(반영 개월수)는 그대로 DP Settings 소관이라
                여기서는 노출하지 않는다(이 화면은 켤지/끌지만 정한다).
                v26.10: POS 미출시라 SHOW_POS 로 내린다. 아래 섹션의 윗선(borderTop)도 이 섹션이 있을 때만. */}
            {SHOW_POS && (
            <Box sx={{ p: 1.5, gridColumn: { md: "span 2" } }}>
              <Box sx={{ ...SUB_HEADER_SX, mb: 1.375 }}>{t("bfSourceData")}</Box>
              <FormControlLabel
                sx={{ ml: "1px", "& .MuiFormControlLabel-label": { fontSize: FS.SUB } }}
                control={<StyledSwitch sx={{ mr: 2 }} checked={posActualFlg} onChange={(e) => setPosActualFlg(e.target.checked)} />}
                label={t("posActualUsage")}
              />
            </Box>
            )}

            <Box sx={{ p: 1.5, gridColumn: { md: "span 2" }, ...(SHOW_POS ? { borderTop: 1, borderColor: "divider" } : null) }}>
              <Box sx={{ ...SUB_HEADER_SX, mb: 1.375 }}>{t("trainingPeriod")}</Box>
              {/*
                * 세 줄을 세로로 세운다.
                * 예전에는 한 줄에 늘어놓아 (1) 라디오 둘과 체크박스가 같은 키로 나란히 서서
                * "둘 중 하나 고르기"와 "따로 켜는 옵션"이 구분되지 않았고, (2) 창이 좁아지면
                * flexWrap 이 아무 데서나 접혀 짝이 흩어졌다.
                *
                * 라벨 열을 grid `auto` 로 두면 **가장 긴 라벨에 맞춰** 폭이 잡혀 입력칸이 한 줄로 떨어진다.
                * 라벨 길이가 언어마다 크게 다르므로(영 "Set Training Start Date" ↔ 한 "학습 시작일 지정")
                * 픽셀로 고정하지 않는다.
                */}
              <Box sx={{
                display: "grid", gridTemplateColumns: "auto auto minmax(0, 1fr)",
                alignItems: "center", justifyItems: "start", columnGap: 1.5, rowGap: 1,
                /* 라디오 라벨 + 입력칸(160) + 범위 안내를 한 줄에 세우려면 ~340px 은 있어야 한다.
                   좁으면 한 칸짜리로 펴서 셋을 세로로 쌓는다 — 안내 문장이 잘리는 것보다 낫다.
                   (자식은 이미 라벨 → 입력칸 → 안내 순서라 그대로 읽힌다.) */
                [CARD_NARROW]: { gridTemplateColumns: "minmax(0, 1fr)" },
              }}>
                <FormControlLabel
                  sx={{ ml: "1px", mr: 0, height: 34, "& .MuiFormControlLabel-label": { fontSize: FS.SUB } }}
                  control={<Radio size="small" checked={trainingType === TRAINING_TYPE.PERIOD}
                    onChange={() => setTrainingType(TRAINING_TYPE.PERIOD)} />}
                  label={t("setTrainingPeriod")}
                />
                <Box sx={{ width: TRAINING_FIELD_WIDTH }}>
                  <TextField
                    variant="standard"
                    size="small" type="number" fullWidth value={trainingPeriod}
                    disabled={trainingType !== TRAINING_TYPE.PERIOD}
                    error={periodInvalid}
                    sx={FIELD_SX}
                    inputProps={{ inputMode: "numeric", min: 12, max: 120, step: "1" }}
                    InputProps={{ endAdornment: <InputAdornment position="end" sx={{ "& p": { fontSize: FS.BODY } }}>{t("months")}</InputAdornment> }}
                    onChange={(event) => setTrainingPeriod(Number(event.target.value))}
                  />
                </Box>
                {/*
                  허용 범위 안내. 예전에는 입력칸 **아래** 에 "12 - 120" 이라고만 적혀 있어서
                  뜻도 안 통하고(무슨 12 인지) 그 줄만 키가 커져 옆 컨트롤과 밑선이 어긋났다.
                  옆에 문장으로 세우면 뜻이 분명하고 행 높이도 34px 로 모두 같아진다.
                */}
                <Box sx={{
                  display: "flex", alignItems: "center", height: 34,
                  fontSize: FS.BODY,
                  color: periodInvalid ? "error.main"
                    : trainingType === TRAINING_TYPE.PERIOD ? "text.secondary" : "text.disabled",
                }}>
                  {t("MSG_EnterIntBetween", { min: 12, max: 120 })}
                </Box>

                <FormControlLabel
                  sx={{ ml: "1px", mr: 0, height: 34, "& .MuiFormControlLabel-label": { fontSize: FS.SUB } }}
                  control={<Radio size="small" checked={trainingType === TRAINING_TYPE.DATE}
                    onChange={() => setTrainingType(TRAINING_TYPE.DATE)} />}
                  label={t("setTrainingStartDate")}
                />
                <Box sx={{ width: TRAINING_FIELD_WIDTH }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={new ZDate(startDate)}
                      maxDate={CURRENT_DATE}
                      format={"yyyy-MM-dd"}
                      views={["year", "month", "day"]}
                      openTo={"day"}
                      disabled={trainingType !== TRAINING_TYPE.DATE}
                      onAccept={newStartValue => setStartDate(dateUtils.formatDate(newStartValue, "yyyy-MM-dd"))}
                      slotProps={{ textField: { variant: "standard", size: "small", fullWidth: true, sx: FIELD_SX } }}
                    />
                  </LocalizationProvider>
                </Box>
                <Box />

                {/* 위 둘 중 하나를 고르는 선택이 아니라 따로 켜는 옵션이라, 한 칸 띄워 라디오 묶음과 떼어 놓는다 */}
                <Tooltip title={t("MSG_TrainingDataCleasing")}>
                  <FormControlLabel
                    sx={{
                      gridColumn: "1 / -1", ml: "1px", mr: 0, mt: 0.75, height: 34,
                      "& .MuiFormControlLabel-label": { fontSize: FS.SUB },
                    }}
                    control={<StyledSwitch sx={{ mr: 2 }} checked={trainingFullData} onChange={(e) => setTrainingFullData(e.target.checked)} />}
                    label={t("trainingDataCleansing")}
                  />
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </StepCard>

        {/* STEP 3 — 예측 */}
        <StepCard
          id={STEP_IDS.S3}
          num={`${t("stepTitle")} 03`}
          title={t("forecasting")}
          summary={stepSummary[STEP_IDS.S3]}
        >
          <Box sx={{ containerType: "inline-size" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", [S3_SPLIT_AT]: { gridTemplateColumns: "minmax(0, 220px) minmax(0, 1fr)" } }}>
            {/* 왼쪽 — 아직 안 쓰는 모델. 누르면 오른쪽으로 넘어간다 */}
            <Box sx={{ p: 1.5, borderRight: 0, [S3_SPLIT_AT]: { borderRight: 1 }, borderColor: "divider", minWidth: 0 }}>
              <Box sx={{ ...SUB_HEADER_SX, mb: 1.125 }}>
                {t("availableModels")}{" "}
                <Box component="span" sx={{ fontWeight: 400, color: "text.secondary" }}>{availableAlgos.length}</Box>
              </Box>
              {/* 행마다 테두리를 두르면 상자가 목록 길이만큼 늘어난다 — 상자는 하나, 행은 선으로 나눈다 */}
              {availableAlgos.length === 0
                ? <Note>{t("MSG_AllModelsInUse")}</Note>
                : (
                  <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                    {availableAlgos.map((algo, idx) => (
                      <Box
                        key={algo.cd}
                        onClick={() => handleEnableAlgorithm(algo.cd)}
                        sx={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          px: 1.125, py: 0.875, fontSize: FS.SUB, color: "text.secondary", cursor: "pointer",
                          borderTop: idx === 0 ? 0 : 1, borderColor: "divider",
                          "&:hover": { bgcolor: "action.hover", color: "text.primary" },
                        }}
                      >
                        <span>{algo.name}</span>
                        <AddIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                      </Box>
                    ))}
                  </Box>
                )}
            </Box>

            {/* 오른쪽 — 쓰는 모델과 각자의 파라미터. 라디오가 기본 모델을 정한다 */}
            <Box sx={{ p: 1.5, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1.125 }}>
                <Box sx={SUB_HEADER_SX}>
                  {t("appliedModels")}{" "}
                  <Box component="span" sx={{ fontWeight: 400, color: "text.secondary" }}>{appliedAlgos.length}</Box>
                </Box>
                <Note>{t("MSG_DefaultModelHint")}</Note>
              </Box>
              {/* containerType — 행 배치를 뷰포트가 아니라 **이 상자의 폭**으로 정한다(APPLIED_ROW_SX 주석).
                  fontSize 는 장식이 아니라 **질의의 단위 기준**이다 — 여기 크기가 곧 위 em 임계값의 1em 이다. */}
              <Box sx={{
                border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden",
                containerType: "inline-size", fontSize: FS.SUB,
              }}>
                {appliedAlgos.map((algo, idx) => {
                  const isDefault = defaultAlgorithm?.cd === algo.cd;
                  return (
                    <Box key={algo.cd} sx={{
                      ...APPLIED_ROW_SX,
                      borderTop: idx === 0 ? 0 : 1, borderColor: "divider",
                      bgcolor: isDefault ? theme => alpha(theme.palette.primary.main, 0.05) : "background.paper",
                    }}>
                      <Radio size="small" sx={{ p: 0, gridArea: "radio" }} checked={isDefault} onChange={() => handleDefaultAlgorithm(algo.cd)} />
                      <Box sx={{ gridArea: "name", fontSize: FS.SUB, fontWeight: 600 }}>{algo.name}</Box>
                      <Box sx={{ gridArea: "badge" }}>
                        {isDefault && (
                          <Box component="span" sx={{
                            fontSize: FS.BODY, fontWeight: 600, borderRadius: "3px", px: 0.75, py: "2px",
                            whiteSpace: "nowrap",
                            color: "primary.contrastText", bgcolor: "primary.main",
                          }}>{t("default")}</Box>
                        )}
                      </Box>
                      <Box sx={{ gridArea: "param", minWidth: 0 }}>{paramCell(algo)}</Box>
                      {/* 행 끝의 아이콘 버튼은 앱 공용 규격을 쓴다(components/ActionIconButton).
                          CloseButton 은 툴팁 span 을 제 루트로 쓰므로 자리 지정은 감싼 칸이 맡는다. */}
                      <Box sx={{ gridArea: "close", display: "flex", justifyContent: "flex-end" }}>
                        <CloseButton
                          label="remove"
                          disabled={isDefault}
                          onClick={() => handleDisableAlgorithm(algo.cd)}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
          </Box>

          {/* 계층별 모델 룰 — 위에서 고른 기본값을 조합 단위로 덮어쓴다.
              이미 STEP 카드 안이라 패널이 제 테두리를 또 두르면 상자가 겹친다 — 윗선 하나로 나눈다 */}
          <Box>
            <BfAlgorithmSettings
              t={t}
              title={t("hrchyModelRule")}
              containerSx={{
                position: "static", maxHeight: "none", overflow: "visible",
                border: 0, borderTop: "1px solid", borderColor: "divider", borderRadius: 0, p: 1.5,
              }}
              initialRules={initialRules}
              rulesReloadKey={rulesReloadKey}
              registerGetRules={(fn) => { getRulesRef.current = fn; }}
              algorithmState={algorithmState}
              targetHrchy={targetHrchy}
              uploadRules={uploadRules}
              onRulesChanged={() => { rulesRestoredRef.current = false; loadAlgoRules(); }}
              onRuleCountChange={handleRuleCountChange}
            />
          </Box>
        </StepCard>

        {/* STEP 4 — 후처리 */}
        <StepCard
          id={STEP_IDS.S4}
          num={`${t("stepTitle")} 04`}
          title={t("postprocessing")}
          summary={stepSummary[STEP_IDS.S4]}
        >
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, flexWrap: "wrap", p: 1.5 }}>
            <FormControlLabel
              sx={{ ml: "1px", height: 34, "& .MuiFormControlLabel-label": { fontSize: FS.SUB } }}
              control={<StyledSwitch sx={{ mr: 2 }} checked={replaceOutlier} onChange={(e) => setReplaceOutlier(e.target.checked)} />}
              label={t("replaceOutlier")}
            />
            <Box sx={{ width: 130 }}>
              <FieldLabel disabled={!replaceOutlier}>Threshold pct</FieldLabel>
              <TextField
                variant="standard" size="small" type="number" fullWidth value={threshold} disabled={!replaceOutlier} sx={FIELD_SX}
                inputProps={{ inputMode: "numeric", min: 0 }}
                onChange={(event) => setThreshold(Number(event.target.value))}
              />
            </Box>
            <Box sx={{ width: 130 }}>
              <FieldLabel disabled={!replaceOutlier}>Multiplier pct</FieldLabel>
              <TextField
                variant="standard" size="small" type="number" fullWidth value={multiplier} disabled={!replaceOutlier} sx={FIELD_SX}
                inputProps={{ inputMode: "numeric", min: 0 }}
                onChange={(event) => setMultiplier(Number(event.target.value))}
              />
            </Box>
            <Note sx={{ maxWidth: 280, pb: 1 }}>{t("MSG_ReplaceOutlierNote")}</Note>
          </Box>
        </StepCard>

        {/* STEP 5 — 모델 선택 */}
        <StepCard
          id={STEP_IDS.S5}
          num={`${t("stepTitle")} 05`}
          title={t("modelSelection")}
          summary={stepSummary[STEP_IDS.S5]}
        >
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, flexWrap: "wrap", p: 1.5 }}>
            <Box sx={{ width: 150 }}>
              <FieldLabel>{t("historyHorizon")}</FieldLabel>
              <TextField
                variant="standard" size="small" type="number" fullWidth value={historyHorizon} sx={FIELD_SX}
                inputProps={{ inputMode: "numeric", min: 0 }}
                onChange={(event) => setHistoryHorizon(Number(event.target.value))}
              />
            </Box>
            <Box sx={{ width: 150 }}>
              <FieldLabel>{t("metrics")}</FieldLabel>
              <Select
                variant="standard" size="small" fullWidth value={metrics} sx={FIELD_SX}
                onChange={(event) => setMetrics(event.target.value)}
              >
                {bfMetric.map(m => <MenuItem key={m} value={m} sx={{ fontSize: FS.SUB }}>{m}</MenuItem>)}
              </Select>
            </Box>
            <Note sx={{ maxWidth: 320, pb: 1 }}>
              {enabledCodes.length > 1
                ? t("MSG_ModelSelectionNote", { count: enabledCodes.length, horizon: historyHorizon, metric: metrics })
                : t("MSG_SingleModelSelectionNote", { model: defaultAlgorithm?.name ?? "-" })}
            </Note>
          </Box>
        </StepCard>
        </Box>
      </Box>

      {/* ── 적용 요약 ─────────────────────────────────────────────── */}
      <Box sx={{
        width: 340, flex: "none", display: "flex", flexDirection: "column",
        bgcolor: "background.paper", borderLeft: 1, borderColor: "divider",
      }}>
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2, py: 1.5, flex: "none", borderBottom: 1, borderColor: "divider",
        }}>
          <Box sx={SUB_HEADER_SX}>{t("appliedSummary")}</Box>
          <Box sx={{ fontSize: FS.BODY, color: "text.disabled" }}>{lastSavedLabel}</Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2, py: 1.5 }}>
          <Box sx={{ fontSize: FS.BODY, fontWeight: 600, color: "text.secondary", mb: 1.25 }}>
            {t("targetHierarchyLevel")} {checkedCells.length} / 9
          </Box>
          <Box sx={{ position: "relative", width: "100%", height: 148 }}>
            <Box sx={{ position: "absolute", left: 0, top: 0, width: "39.33%", display: "flex", flexDirection: "column", gap: 1.25 }}>
              {LEVELS.map(l => levelPill(l, usedCust.has(l)))}
            </Box>
            <Box sx={{ position: "absolute", right: 0, top: 0, width: "39.33%", display: "flex", flexDirection: "column", gap: 1.25 }}>
              {LEVELS.map(l => levelPill(l, usedItem.has(l)))}
            </Box>
            <Box component="svg" width="100%" height="148" viewBox="0 0 300 148" preserveAspectRatio="none"
              sx={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}>
              {linkPaths.map(l => (
                <path key={l.key} d={l.d} stroke="rgba(4, 139, 168, 0.7)" strokeWidth="2" fill="none" strokeLinecap="round" />
              ))}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2.5, mt: 1.75, py: 1.5, borderTop: 1, borderBottom: 1, borderColor: "divider" }}>
            {[
              { label: t("targetCombination"), value: `${checkedCells.length} / 9` },
              { label: t("trainingPeriod"), value: trainingType === TRAINING_TYPE.PERIOD ? `${trainingPeriod}${t("months")}` : startDate },
              { label: t("metrics"), value: metrics },
            ].map(s => (
              <Box key={s.label}>
                <Box sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{s.label}</Box>
                <Box sx={{ fontSize: FS.H1, fontWeight: 700, mt: "3px" }}>{s.value}</Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ py: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ fontSize: FS.BODY, fontWeight: 600, color: "text.secondary", mb: 1 }}>
              {t("appliedModels")} {appliedAlgos.length}
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {appliedAlgos.map(algo => (
                <Box key={algo.cd} sx={{
                  fontSize: FS.BODY, borderRadius: "3px", px: 0.875, py: "3px",
                  ...tintChipSx(algorithmColor[algo.name]),
                }}>
                  {algo.cd === defaultAlgorithm?.cd ? `${algo.name} · ${t("default")}` : algo.name}
                </Box>
              ))}
            </Box>
            {ruleCount > 0 && <Note sx={{ mt: 1 }}>{t("MSG_RuleOverridesDefault", { count: ruleCount })}</Note>}
          </Box>

          <Box sx={{ py: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ fontSize: FS.BODY, fontWeight: 600, color: "text.secondary", mb: 1 }}>
              {t("needsReview")} {issues.length}
            </Box>
            {issues.length === 0
              ? <Note>{t("MSG_NoIssue")}</Note>
              : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {issues.map(w => (
                    <Box key={w.text} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <Box sx={{
                        width: 6, height: 6, borderRadius: "50%", mt: "5px", flex: "none",
                        bgcolor: w.level === "error" ? "error.main" : "warning.main",
                      }} />
                      <Box sx={{ fontSize: FS.SUB, lineHeight: 1.6 }}>{w.text}</Box>
                    </Box>
                  ))}
                </Box>
              )}
          </Box>

          <Box sx={{ py: 1.5 }}>
            <Box sx={{ fontSize: FS.BODY, fontWeight: 600, color: "text.secondary", mb: 1 }}>
              {t("changes")} {changes.length}
            </Box>
            {changes.length === 0
              ? <Note>{t("MSG_NoChangeToSave")}</Note>
              : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.625 }}>
                  {changes.map(c => (
                    <Box key={`${c.step}-${c.label}`} sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                      <Box sx={{ fontSize: FS.BODY, color: "text.secondary", width: 104, flex: "none" }}>{c.label}</Box>
                      <Box sx={{ fontSize: FS.SUB }}>{c.delta}</Box>
                    </Box>
                  ))}
                </Box>
              )}
          </Box>
        </Box>

        <Box sx={{
          display: "flex", gap: 1, px: 2, py: 1.5, flex: "none",
          borderTop: 1, borderColor: "divider", bgcolor: "grey.50",
        }}>
          {/*
            저장은 "변경 사항"이 비어 있어도 열어 둔다 — 아래쪽 계층별 모델 룰은 자식이 들고 있어서
            여기 변경 목록에 잡히지 않는다. 막을 수 있는 건 값이 확실히 틀린 학습 기간뿐이다.
            반대로 초기화는 이 화면이 추적하는 스텝 설정만 되돌리므로 dirty 를 그대로 쓴다.
          */}
          <Button
            fullWidth variant="contained" color="primary" disableElevation
            startIcon={<SaveIcon />} disabled={periodInvalid}
            onClick={handleDialogOpen}
          >
            {t("save")}
          </Button>
          <Button
            variant="outlined" color="inherit" startIcon={<RestartAltIcon />}
            disabled={!dirty} sx={{ flex: "none", whiteSpace: "nowrap" }}
            onClick={handleReset}
          >
            {t("reset")}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
    </Box>
  );
};

export default withTranslation()(BfSettings);
